import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { assistantsService } from '../../services/api/assistants';

interface Props {
  conversationId: number;
  messageId: number;
}

/**
 * Aperçu du document Word tel qu'il sera téléchargé.
 *
 * Le rendu Markdown montre le contenu ; celui-ci montre le fichier — en-tête
 * institutionnel, marges A4, tableaux Word. C'est ce que la personne ouvrira
 * dans Word, affiché avant de le télécharger.
 *
 * Le rendu se fait dans le navigateur : pas de conversion serveur, donc pas de
 * dépendance à LibreOffice.
 */
const ApercuDocx: React.FC<Props> = ({ conversationId, messageId }) => {
  const conteneur = useRef<HTMLDivElement>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let annule = false;

    const rendre = async () => {
      try {
        const blob = await assistantsService.telechargerBlob(conversationId, messageId);
        if (annule || !conteneur.current) return;

        // Import à la demande : la bibliothèque pèse lourd et ne sert qu'ici.
        const { renderAsync } = await import('docx-preview');
        conteneur.current.innerHTML = '';
        await renderAsync(blob, conteneur.current, undefined, {
          className: 'docx',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: true,
          breakPages: true,
        });
      } catch (e: any) {
        if (!annule) setErreur(e.message || "L'aperçu n'a pas pu être généré.");
      } finally {
        if (!annule) setChargement(false);
      }
    };

    rendre();
    return () => { annule = true; };
  }, [conversationId, messageId]);

  return (
    <div className="rounded-md border border-eg-bord bg-eg-voile p-4">
      {chargement && (
        <div className="flex items-center justify-center gap-3 py-12 text-sm text-eg-gris">
          <Loader2 className="w-5 h-5 animate-spin" />
          Préparation de l'aperçu...
        </div>
      )}
      {erreur && <p className="py-4 text-sm text-eg-accent">{erreur}</p>}
      <div ref={conteneur} className="overflow-x-auto" />
    </div>
  );
};

export default ApercuDocx;
